import React from 'react'
import { useDirtyForm } from '@/contexts/DirtyFormContext'

interface DirtyKeyDescriptor {
  table: string
  id:    string
  field: string
}

interface Props {
  value:     'Y' | 'N' | null
  onChange?: (v: 'Y' | 'N' | null) => void
  disabled?: boolean
  // When set + a DirtyFormProvider is in scope, clicks stage drafts into
  // the page-scoped batch instead of calling onChange. Falls through to
  // direct onChange when no provider is mounted, so non-batched callers
  // (modals, legacy pages) keep working unchanged.
  dirtyKey?: DirtyKeyDescriptor
}

export const EditablePill: React.FC<Props> = ({ value, onChange, disabled, dirtyKey }) => {
  const dirty = useDirtyForm()
  const usingDirty = dirtyKey !== undefined && dirty.isUnderDirtyForm
  const draftEntry = usingDirty ? dirty.getDraft(dirtyKey!.table, dirtyKey!.id, dirtyKey!.field) : undefined
  const displayValue: 'Y' | 'N' | null = usingDirty
    ? (draftEntry ? (draftEntry.draftValue as 'Y' | 'N' | null) : value)
    : value

  const isDirty = usingDirty && draftEntry !== undefined
  const ringClass = isDirty ? 'ring-1 ring-amber-300 dark:ring-amber-600' : ''

  if (disabled) {
    if (displayValue === 'Y') return <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 ${ringClass}`}>YES</span>
    if (displayValue === 'N') return <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 ${ringClass}`}>NO</span>
    return <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] border border-dashed border-slate-300 dark:border-slate-600 text-slate-300 dark:text-slate-500 ${ringClass}`}>&mdash;</span>
  }

  const apply = (v: 'Y' | 'N' | null) => {
    if (usingDirty) {
      dirty.setDraft(dirtyKey!.table, dirtyKey!.id, dirtyKey!.field, value, v)
      return
    }
    if (onChange) onChange(v)
  }

  return (
    <div className={`inline-flex rounded-full overflow-hidden border border-slate-200 dark:border-slate-700 ${ringClass}`}>
      <button
        onClick={() => apply(displayValue === 'Y' ? null : 'Y')}
        className={`px-2 py-0.5 text-[10px] font-semibold transition-colors ${
          displayValue === 'Y'
            ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300'
            : 'text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700'
        }`}
      >Y</button>
      <button
        onClick={() => apply(displayValue === 'N' ? null : 'N')}
        className={`px-2 py-0.5 text-[10px] font-semibold border-l border-slate-200 dark:border-slate-700 transition-colors ${
          displayValue === 'N'
            ? 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200'
            : 'text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700'
        }`}
      >N</button>
    </div>
  )
}
